import { ImageIcon, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import type { ImageInfo } from "../api/message";
import config from "../config";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadNew: (file: File) => Promise<void>;
  onSelectExisting: (messageId: string) => Promise<void>;
  existingImages: ImageInfo[];
  isUploading?: boolean;
}

export function ImageUploadModal({
  isOpen,
  onClose,
  onUploadNew,
  onSelectExisting,
  existingImages,
  isUploading = false,
}: ImageUploadModalProps) {
  const [selectedTab, setSelectedTab] = useState<"upload" | "existing">("upload");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("PNG、JPG形式のファイルのみアップロード可能です");
      return;
    }

    const maxSize = config.image.maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`ファイルサイズは${config.image.maxSizeMB}MB以下にしてください`);
      return;
    }

    try {
      await onUploadNew(file);
      onClose();
    } catch (error) {
      console.error("Failed to upload image:", error);
    }
  };

  const handleSelectExisting = async () => {
    if (!selectedImage) {
      toast.error("画像を選択してください");
      return;
    }

    try {
      await onSelectExisting(selectedImage);
      onClose();
    } catch (error) {
      console.error("Failed to send existing image:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>画像を送信</DialogTitle>
        </DialogHeader>

        <Tabs value={selectedTab} onValueChange={(v) => setSelectedTab(v as "upload" | "existing")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">新規アップロード</TabsTrigger>
            <TabsTrigger value="existing">履歴から選択</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-4">
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg">
              <Upload className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-4">PNG、JPG形式（最大{config.image.maxSizeMB}MB）</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                {isUploading ? "アップロード中..." : "ファイルを選択"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="existing" className="mt-4">
            {existingImages.length === 0 ? (
              <div className="text-center py-8">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">送信された画像がありません</p>
              </div>
            ) : (
              <>
                <ScrollArea className="h-[300px] w-full rounded-md border p-4">
                  <div className="grid grid-cols-3 gap-4">
                    {existingImages.map((image) => (
                      <div
                        key={image.message_id}
                        className={`cursor-pointer rounded-lg overflow-hidden border-2 ${
                          selectedImage === image.message_id ? "border-blue-500" : "border-gray-200"
                        }`}
                        onClick={() => setSelectedImage(image.message_id)}
                      >
                        <img src={image.image_url} alt="Past upload" className="w-full h-24 object-cover" />
                        <div className="p-2 text-xs text-gray-500">
                          {new Date(image.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="flex justify-end mt-4">
                  <Button onClick={handleSelectExisting} disabled={!selectedImage}>
                    選択した画像を送信
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
